import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import 'prop-types';
import * as React from 'react';
import { KEYS } from '../constants/keys.js';
import { mergeProps } from '../utils/react.js';

var ITEM_ROLES = {
  menu: ["menuitem", "menuitemradio", "menuitemcheckbox"],
  listbox: ["option"]
};
var getElementOwner = function getElementOwner(el) {
  return el && el.ownerDocument || document;
};

var isElementDisabled = function isElementDisabled(el) {
  return el && (el.getAttribute("disabled") === "true" || el.getAttribute("aria-disabled") === "true");
};

var getNodeAsElement = function getNodeAsElement(node) {
  return node && node.nodeType === Node.ELEMENT_NODE ? node : void 0;
};

var scrollToElement = function scrollToElement(scrollParent, el) {
  if (!el) return;
  var elemTop = el.offsetTop - scrollParent.offsetTop;
  var elemBottom = elemTop + el.offsetHeight;
  var shouldScrollDown = elemBottom >= scrollParent.offsetHeight + el.scrollTop;
  var shouldScrollUp = elemTop <= scrollParent.scrollTop;

  if (shouldScrollDown) {
    scrollParent.scrollTop = el.offsetTop - scrollParent.offsetHeight + el.offsetHeight;
  }

  if (shouldScrollUp) {
    scrollParent.scrollTop = elemTop;
  }
};

var getActiveItem = function getActiveItem(list, items) {
  var owner = getElementOwner(list);
  var focused = getNodeAsElement(owner.activeElement);
  if (!focused) return;
  return items[items.indexOf(focused)];
};

var getNextItem = function getNextItem(items, item, reverse) {
  if (!item) return;
  var itemIndex = items.indexOf(item);
  if (itemIndex === -1) return;
  var sibling = items[reverse ? itemIndex - 1 : itemIndex + 1];
  var disabled = isElementDisabled(sibling);

  if (item && sibling && !disabled) {
    return sibling;
  }

  if (item && sibling && disabled) {
    return getNextItem(items, sibling, reverse);
  }

  if (item && !sibling) {
    var child = reverse ? items[items.length - 1] : items[0];
    return isElementDisabled(child) ? getNextItem(items, child, reverse) : getNodeAsElement(child);
  }
};

var getFirstItem = function getFirstItem(items) {
  return getNextItem(items, items[items.length - 1]);
};

var moveItem = function moveItem(item, e) {
  if (!item) return;
  item.focus();
  e === null || e === void 0 ? void 0 : e.preventDefault();
};

var handleNavKeys = function handleNavKeys(e, items, item) {
  if (!item) return;
  var nextItem;

  switch (e.key) {
    case KEYS.ARROW_UP:
      {
        nextItem = getNextItem(items, item, true);
        break;
      }

    case KEYS.ARROW_DOWN:
      {
        nextItem = getNextItem(items, item);
        break;
      }

    case KEYS.HOME:
      {
        nextItem = getFirstItem(items);
        break;
      }

    case KEYS.END:
      {
        nextItem = getNextItem(items, getNodeAsElement(items[0]), true);
        break;
      }
  }

  moveItem(nextItem, e);
};

var handleSearchKeys = function handleSearchKeys(e, items, item) {
  if (Object.entries(KEYS).some(function (_ref) {
    var _ref2 = _slicedToArray(_ref, 2),
        key = _ref2[1];

    return e.key === key;
  })) return;
  if (!item) return;
  var nextItem;
  var match;

  while (!match && nextItem !== item) {
    var _nextItem, _nextItem2;

    nextItem = getNextItem(items, nextItem || item);
    var innerText = (_nextItem = nextItem) === null || _nextItem === void 0 ? void 0 : _nextItem.innerText;
    var textContent = ((_nextItem2 = nextItem) === null || _nextItem2 === void 0 ? void 0 : _nextItem2.textContent) || "";
    var text = innerText ? innerText : textContent;
    match = text.toLowerCase().startsWith(e.key.toLowerCase());
  }

  moveItem(nextItem);
};

function useMenuNav(_ref3) {
  var children = _ref3.children,
      _ref3$role = _ref3.role,
      role = _ref3$role === void 0 ? "menu" : _ref3$role;
  var menuRef = React.useRef(null);
  var childrenLength = React.Children.toArray(children).length;
  var itemRefs = React.useMemo(function () {
    return Array.from({
      length: childrenLength
    }, function () {
      return /*#__PURE__*/React.createRef();
    });
  }, [childrenLength]);
  var itemRoles = ITEM_ROLES[role];

  var getListItems = function getListItems() {
    return itemRefs.filter(function (item) {
      return item.current && itemRoles.includes(item.current.getAttribute("role") || "");
    }).map(function (item) {
      return item.current;
    });
  };

  var handleFocus = function handleFocus(e) {
    if (e.target !== e.currentTarget) return;
    if (itemRefs.filter(function (item) {
      return item.current && !isElementDisabled(item.current);
    }).length === 0) return;
    moveItem(getFirstItem(getListItems()));
  };

  var handleKeyDown = function handleKeyDown(e) {
    var items2 = getListItems();
    var item = getActiveItem(menuRef.current, items2);
    handleNavKeys(e, items2, item);
    handleSearchKeys(e, items2, item);
  };

  var handleItemFocus = function handleItemFocus() {
    var menu = menuRef.current;
    var items2 = getListItems();
    var item = getActiveItem(menuRef.current, items2);
    scrollToElement(menu, item);
  };

  var items = React.Children.map(children, function (child, index) {
    if (! /*#__PURE__*/React.isValidElement(child)) return child;
    var item = child;
    return /*#__PURE__*/React.cloneElement(item, mergeProps({
      onFocus: item.props.onFocus,
      ref: item.ref
    }, {
      role: item.props.role ? item.props.role : itemRoles[0],
      ref: itemRefs[index],
      tabIndex: item.props.tabIndex ? item.props.tabIndex : -1,
      onFocus: handleItemFocus
    }));
  });
  return {
    onFocus: handleFocus,
    onKeyDown: handleKeyDown,
    children: items,
    ref: menuRef,
    role: role,
    tabIndex: 0
  };
}

export { getActiveItem, getElementOwner, getNodeAsElement, useMenuNav };
